/**
 * useSidebarPreview - Live sidebar resolver preview
 *
 * Wraps sidebarAPI.resolve() with debounce for live preview during
 * drag-and-drop sidebar building. Avoids excessive API calls.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { sidebarAPI } from '../../api/admin.js';

const DEBOUNCE_MS = 300;

export function useSidebarPreview() {
  const [resolvedGroups, setResolvedGroups] = useState(null);
  const [meta, setMeta] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [isResolving, setIsResolving] = useState(false);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);
  const abortRef = useRef(null);

  // Resolve sidebar with given context (debounced)
  const resolve = useCallback((context = {}) => {
    // Clear previous timer
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(async () => {
      // Abort previous in-flight request
      if (abortRef.current) abortRef.current.aborted = true;
      const thisRequest = { aborted: false };
      abortRef.current = thisRequest;

      setIsResolving(true);
      setError(null);
      try {
        const res = await sidebarAPI.resolve(context);
        if (thisRequest.aborted) return; // Stale response

        if (res.ok) {
          setResolvedGroups(res.data?.groups || res.data || null);
          setMeta(res.data?.meta || null);
          setWarnings(res.data?.warnings || []);
        } else {
          setError(res.error || 'Resolver error');
        }
      } catch (err) {
        if (!thisRequest.aborted) {
          setError(err.message || 'Network error');
        }
      } finally {
        if (!thisRequest.aborted) {
          setIsResolving(false);
        }
      }
    }, DEBOUNCE_MS);
  }, []);

  // Validate override without applying
  const validate = useCallback(async (override) => {
    try {
      const res = await sidebarAPI.validate(override);
      return res;
    } catch (err) {
      return { ok: false, errors: [err.message] };
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.aborted = true;
    };
  }, []);

  return {
    resolvedGroups,
    meta,
    warnings,
    isResolving,
    error,
    resolve,
    validate,
  };
}

export default useSidebarPreview;
